/**
 * QuarterlyBreakdown Component
 * Used in: Dashboard screen - displays Quarterly Financial Performance
 */
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FinancialData } from '../data/financialData';
import { MobileChartFallback } from './MobileChartFallback';
import { useScreenSize } from '../hooks/useScreenSize';
import { TrendingUp } from 'lucide-react';

interface QuarterlyBreakdownProps {
  data: FinancialData[];
}

export const QuarterlyBreakdown: React.FC<QuarterlyBreakdownProps> = ({ data }) => {
  const { shouldShowFallback } = useScreenSize();
  
  // Calculate quarterly data from the input data
  const quarterlyData = React.useMemo(() => {
    const quarterlyGroups = data.reduce((acc, item) => {
      if (!acc[item.quarter]) {
        acc[item.quarter] = [];
      }
      acc[item.quarter].push(item);
      return acc;
    }, {} as Record<string, FinancialData[]>);

    return Object.entries(quarterlyGroups).map(([quarter, quarterData]) => {
      const count = quarterData.length;
      return {
        quarter,
        totalIncome: quarterData.reduce((sum, item) => sum + item.totalIncome, 0) / count,
        totalCOGS: quarterData.reduce((sum, item) => sum + item.totalCOGS, 0) / count,
        grossProfit: quarterData.reduce((sum, item) => sum + item.grossProfit, 0) / count,
        totalExpense: quarterData.reduce((sum, item) => sum + item.totalExpense, 0) / count,
        netOrdinaryIncome: quarterData.reduce((sum, item) => sum + item.netOrdinaryIncome, 0) / count,
      };
    }).sort((a, b) => a.quarter.localeCompare(b.quarter));
  }, [data]);
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Show fallback for very narrow screens
  if (shouldShowFallback) {
    return (
      <MobileChartFallback
        title="Quarterly Financial Performance"
        data={quarterlyData}
        dataKeys={['totalIncome', 'totalCOGS', 'netOrdinaryIncome']}
        formatValue={formatCurrency}
        showRotateMessage={true}
      />
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-green-600" />
        Quarterly Financial Performance
      </h3>
      <div className="chart-container w-full overflow-x-auto">
        <ResponsiveContainer width="100%" height={450} minWidth={300}>
          <BarChart data={quarterlyData} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="quarter" 
            stroke="#6b7280"
            fontSize={10}
            angle={-45}
            textAnchor="end"
            height={60}
            interval={0}
          />
          <YAxis 
            stroke="#6b7280"
            fontSize={12}
            tickFormatter={formatCurrency}
            domain={[-50000, 'dataMax']}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          
          <Bar 
            dataKey="totalIncome" 
            name="Total Income"
            fill="#3b82f6"
            radius={[2, 2, 0, 0]}
          />
          <Bar 
            dataKey="totalCOGS" 
            name="Total COGS"
            fill="#ef4444"
            radius={[2, 2, 0, 0]}
          />
          <Bar 
            dataKey="netOrdinaryIncome" 
            name="Net Income"
            fill="#8b5cf6"
            radius={[2, 2, 0, 0]}
          />
        </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};